/**
 * Commandes passées — règles reprises de l'app mobile, règle pour règle :
 *   src/components/orders/PastOrderCard.tsx   (carte de l'onglet Passées)
 *   app/(profile)/order-detail.tsx           (détail, avis, commander à nouveau)
 *
 * Les écarts du mobile sont gardés tels quels (voir orders-page-status) :
 * la carte affiche `subtotal ?? total ?? amount`, le détail
 * `total ?? amount ?? subtotal`.
 */

import { parseOrderDate } from "./orders";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
};

/** « WOSH-4821 », ou « #812 » quand le serveur n'a pas d'identifiant WOSH. */
export const pastOrderId = (order) => order?.wosh_order_id ?? `#${order?.id}`;

/** Date affichée sur la carte : livraison, sinon collecte, sinon mise à jour. */
export const pastOrderDate = (order) =>
  order?.delivery_date ?? order?.pickup_date ?? order?.updated_at?.slice(0, 10);

/**
 * Une facture PEPPOL ne peut être générée que dans le mois civil de la
 * livraison (paiement encaissé) : le mois suivant, la TVA est clôturée.
 */
export function isPeppolExpired(order, now = new Date()) {
  const date = parseOrderDate(order?.delivery_date ?? order?.pickup_date);
  if (!date) return false;
  return (
    date.getFullYear() < now.getFullYear() ||
    (date.getFullYear() === now.getFullYear() && date.getMonth() < now.getMonth())
  );
}

const lines = (order) =>
  order?.product_orders?.length ? order.product_orders : order?.products ?? [];

const linePrice = (line) => toNumber(line?.product_price ?? line?.product?.price) || 0;

/** « €12,00 », ou « — » quand le montant manque (fmtEuro du mobile). */
export const formatEuro = (value) => {
  const n = toNumber(value);
  return n === null ? "—" : `€${n.toFixed(2).replace(".", ",")}`;
};

/** Montant de la carte : `subtotal ?? total ?? amount`, rien s'il manque. */
export const cardTotal = (order) => {
  const n = toNumber(order?.subtotal ?? order?.total ?? order?.amount);
  return n === null ? "" : formatEuro(n);
};

/** Nom d'une ligne dans la langue affichée (productName du détail mobile). */
export function lineName(line, lang) {
  if (lang === "nl") {
    return line?.product_name_nl ?? line?.product_name ?? line?.product?.name_nl ?? line?.product?.name ?? "";
  }
  if (lang === "en") {
    return line?.product_name_en ?? line?.product_name ?? line?.product?.name ?? "";
  }
  return line?.product_name ?? line?.product?.name_fr ?? line?.product?.name ?? "";
}

/** Picto servi par l'API : absolu tel quel, sinon rattaché à l'hôte de l'API. */
export function iconUrl(path, apiURL = "") {
  if (!path) return undefined;
  if (/^https?:\/\//.test(path)) return path;
  const host = String(apiURL).replace(/\/api\/v\d+\/?$/, "").replace(/\/$/, "");
  return `${host}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** Lignes du résumé : nom, « 2 × €6,00 », total de ligne, picto éventuel. */
export function summaryItems(order, lang, apiURL) {
  return lines(order).map((line) => {
    const name = lineName(line, lang) || `#${line.product_id}`;
    const price = linePrice(line);
    return {
      label: name,
      initial: (name[0] ?? "?").toUpperCase(),
      meta: price > 0 ? `${line.quantity} × ${formatEuro(price)}` : `× ${line.quantity}`,
      price: price > 0 ? formatEuro(price * line.quantity) : "",
      icon: iconUrl(line?.product?.dark_icon ?? line?.product?.icon, apiURL),
    };
  });
}

/** Sous-total et total payé du détail, dans l'ordre de repli du mobile. */
export function summaryTotals(order) {
  const sum = lines(order).reduce((acc, line) => acc + linePrice(line) * (Number(line.quantity) || 0), 0);
  const total = order?.total ?? order?.amount ?? order?.subtotal ?? (sum || undefined);
  return { subtotal: order?.subtotal ?? total, total };
}

/**
 * « Commander à nouveau » : le panier devient exactement la commande
 * (clear + loadFromOrder du mobile). Le panier web vit sur le serveur, en
 * un seul PUT /customer/cart, avec deux contraintes du backend :
 *  - une ligne par produit (unicité cart_id + product_id, validée avant les
 *    suppressions) : un produit déjà au panier est mis à jour sur sa ligne,
 *    jamais supprimé puis recréé ;
 *  - un produit absent de la blanchisserie fait échouer toute la requête :
 *    seuls les produits du catalogue actuel (`availableIds`) sont repris.
 *
 * @returns {{ attributes: Array, added: number, skipped: number[] }}
 */
export function reorderCart(order, cartItems = [], laundryPlaceId = null, availableIds = null) {
  const wanted = new Map();
  const skipped = [];
  lines(order).forEach((line) => {
    const id = line?.product_id;
    const quantity = Number(line?.quantity) || 0;
    if (!id || quantity <= 0) return;
    if (availableIds && !availableIds.has(id)) {
      if (!skipped.includes(id)) skipped.push(id);
      return;
    }
    wanted.set(id, (wanted.get(id) || 0) + quantity);
  });

  const attributes = [];
  const inCart = new Set();
  (cartItems || []).forEach((item) => {
    const id = item?.product_id ?? item?.id;
    if (!item?.cart_id) return;
    if (wanted.has(id)) {
      inCart.add(id);
      attributes.push({ id: item.cart_id, quantity: wanted.get(id) });
    } else {
      attributes.push({ id: item.cart_id, _destroy: 1 });
    }
  });
  wanted.forEach((quantity, id) => {
    if (!inCart.has(id)) {
      attributes.push({ product_id: id, quantity, laundry_place_id: laundryPlaceId });
    }
  });

  return { attributes, added: wanted.size, skipped };
}

/** Sous-titre de la carte d'avis (RatingCard du mobile). */
export function reviewSubKey({ pending, submitted, editing, rating }) {
  if (pending) return "orderDetail.saving";
  if (submitted && !editing) return "orderDetail.thanksFeedback";
  if (editing) return "orderDetail.updateReview";
  if (rating > 0) return "orderDetail.tellUsMore";
  return "orderDetail.howWasIt";
}

export const REVIEW_MAX = 400;

/** Page d'avis Google proposée après 5 étoiles (même lien que le mobile). */
export const GOOGLE_REVIEW_URL = "https://g.page/r/CeJ0UhgWwL0fEBM/review";
